/** --Dev Note: Add / Update component as per the requirement in the future */
import React, { useState } from 'react';

import { Divider, IconButton, Menu, MenuItem } from '@mui/material';
import clsx from 'clsx';
import Tooltip from 'components/Tooltip';
import PropTypes from 'prop-types';

import classes from './customKebabMenu.module.css';

import { ReactComponent as MoreVertIcon } from 'asset/IconsPack/hx_dots-vertical-filled.svg';

const CustomKebabMenu = ({ menuId, items, moreIconClass, buttonClassName, menuClassName }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const handleClick = (event) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (event) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const onClickExtended = (event, callback) => {
        callback(event);
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton
                className={clsx(classes.iconBtn, buttonClassName)}
                aria-controls={openMenu ? menuId : undefined}
                aria-expanded={openMenu ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                disableRipple
                disableFocusRipple
            >
                <MoreVertIcon className={moreIconClass} />
            </IconButton>
            <Menu
                id={menuId}
                className={clsx(classes.menu, menuClassName)}
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {items.map((item) => {
                    const ItemIcon = item.icon;
                    // A real anchor for `href` items — Setup Guide-style links keep
                    // native right-click / middle-click "open in new tab" behavior
                    // that a plain onClick + window.open would lose.
                    const anchorProps = item.href
                        ? { component: 'a', href: item.href, target: '_blank', rel: 'noreferrer' }
                        : {};

                    const menuItem = (
                        <MenuItem
                            key={item.id}
                            onClick={
                                item.href
                                    ? handleClose
                                    : (e) => !item.disabled && onClickExtended(e, item.onClick)
                            }
                            disabled={item.disabled}
                            className={clsx(classes.menuItem, { [classes.danger]: item.danger })}
                            {...anchorProps}
                        >
                            {ItemIcon && (
                                <span className={classes.menuItemIconWrap}>
                                    <ItemIcon className={classes.menuItemIcon} />
                                </span>
                            )}
                            {item.name}
                        </MenuItem>
                    );

                    const wrapped =
                        item.disabled && item.tooltip ? (
                            // Disabled MUI elements don't fire pointer events, so the
                            // tooltip trigger has to sit on a wrapping span instead.
                            <Tooltip key={item.id} title={item.tooltip} arrow placement="left">
                                <span>{menuItem}</span>
                            </Tooltip>
                        ) : (
                            menuItem
                        );

                    if (!item.divider) return wrapped;

                    return (
                        <React.Fragment key={`${item.id}-wrap`}>
                            {wrapped}
                            <Divider className={classes.menuDivider} />
                        </React.Fragment>
                    );
                })}
            </Menu>
        </>
    );
};

CustomKebabMenu.propTypes = {
    menuId: PropTypes.string,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.node,
            onClick: PropTypes.func,
            danger: PropTypes.bool,
            icon: PropTypes.elementType,
            disabled: PropTypes.bool,
            tooltip: PropTypes.node,
            href: PropTypes.string,
            divider: PropTypes.bool,
        })
    ),
    moreIconClass: PropTypes.string,
    buttonClassName: PropTypes.string,
    menuClassName: PropTypes.string,
};

export default CustomKebabMenu;
